import { useEffect, useRef, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import AmbientBackground from './components/AmbientBackground'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import QuranicVerseBanner from './components/QuranicVerseBanner'
import Home from './pages/Home'
import FlyToCartLayer from './components/FlyToCartLayer'
import WhatsAppButton from './components/WhatsAppButton'
import ProtectedRoute from './components/ProtectedRoute'

// Home is the page most visitors land on (and the one PageSpeed measures),
// so it stays in the main bundle. Every other route is split into its own
// chunk so a first-time visitor to "/" never downloads Shop/Cart/Checkout/
// Blog/etc code they haven't asked for yet.
const Shop = lazy(() => import('./pages/Shop'))
const ProductDetail = lazy(() => import('./pages/ProductDetail'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const PreOrder = lazy(() => import('./pages/PreOrder'))
const Cart = lazy(() => import('./pages/Cart'))
const Checkout = lazy(() => import('./pages/Checkout'))
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'))
const TrackOrder = lazy(() => import('./pages/TrackOrder'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const Contact = lazy(() => import('./pages/Contact'))
const DeliveryInfo = lazy(() => import('./pages/DeliveryInfo'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'))
const NotFound = lazy(() => import('./pages/NotFound'))
// Local SEO landing pages (/fresh-vegetables-karachi and friends) - one
// component, one route per slug, content in src/data/localLandingPages.js.
const LocalLanding = lazy(() => import('./pages/LocalLanding'))

// Admin/in-shop tooling is never seen by customers (barcode/QR generation,
// serial scale handling, the full admin dashboard) - split out of the main
// bundle instead of shipping it to every visitor. See ProtectedRoute below
// for the loading fallback while a chunk is fetched.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminLogin = lazy(() => import('./pages/AdminLogin'))
const InShopSales = lazy(() => import('./pages/InShopSales'))
import { CartProvider } from './context/CartContext'
import { AppProvider } from './context/AppContext'
import { AuthProvider } from './context/AuthContext'
import { logPageView } from './firebase'
import { initMetaPixel, trackPixelPageView } from './utils/metaPixel'
import { captureAttribution } from './utils/attribution'
import { SITE_URL } from './config/siteConfig'
import { LOCAL_LANDING_PAGES } from './data/localLandingPages'

// Shown briefly while a route's chunk downloads.
function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-fresh-green border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function AppLayout() {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/in-shop-sales'
  const isFirstRender = useRef(true)

  useEffect(() => {
    initMetaPixel() // no-op until a real Pixel ID is configured
    // Captures how this visit arrived (Meta ad, Google, organic social,
    // direct...) so Checkout.jsx can attach it to the order if one gets
    // placed. Mount-only is correct here, not per-navigation: an ad click
    // always carries fbclid/gclid/utm params in from outside the app,
    // which means a real browser navigation and a fresh mount of this
    // component - there's no scenario where a new ad click arrives via an
    // internal SPA route change instead. See attribution.js for the
    // re-capture/expiry rules this feeds into.
    captureAttribution()
  }, [])

  useEffect(() => {
    // Firebase Analytics doesn't auto-track client-side route changes in an
    // SPA, so every path change (including the first) is logged explicitly.
    logPageView(location.pathname)

    if (isFirstRender.current) {
      isFirstRender.current = false
      return // Meta Pixel's first PageView was already sent by initMetaPixel above
    }
    trackPixelPageView()
  }, [location.pathname])

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <link rel="canonical" href={`${SITE_URL}${location.pathname}`} />
      </Helmet>
      <AmbientBackground />
      <FlyToCartLayer />
      {!isAdminRoute && <WhatsAppButton />}
      <div className="relative z-10 flex flex-col min-h-screen">
        {!isAdminRoute && <QuranicVerseBanner />}
        {!isAdminRoute && <Navbar />}
        <main className="flex-grow">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              {/* Local SEO landing pages. Declared before the catch-all so
                  each slug resolves to its own page, and generated from the
                  same data the pre-renderer and sitemap use, so the three
                  can never drift apart. */}
              {LOCAL_LANDING_PAGES.map((page) => (
                <Route key={page.slug} path={`/${page.slug}`} element={<LocalLanding slug={page.slug} />} />
              ))}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/preorder" element={<PreOrder />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/delivery" element={<DeliveryInfo />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/in-shop-sales"
                element={
                  <ProtectedRoute>
                    <InShopSales />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <CartProvider>
          <Router>
            <AppLayout />
          </Router>
        </CartProvider>
      </AppProvider>
    </AuthProvider>
  )
}

export default App